/**
 * HeroDemo — auto-playing animated UI demo for the marketing landing hero.
 * Cycles through Dashboard → AI Assessment → Statutory Tracker → PDF Report.
 * Two style variants: "clean" (silent auto-loop) and "captioned" (feature captions).
 * Pure CSS/motion — no video files, no recording. Brand: navy #0B1020 / mint #00F2C3.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, ScrollText, FileText, Sparkles, Camera, CircleCheck, CircleX,
  Timer, Bell, Download, ClipboardList, ShieldCheck, ChevronRight,
} from "lucide-react";

const NAVY = "#0B1020";
const MINT = "#00F2C3";
const PANEL = "#13182A";
const DARK = "#0E1526";
const ROW = "#1A2036";

const SCENES = [
  { key: "dashboard", nav: 0, icon: LayoutDashboard, title: "Live Compliance Dashboard",
    caption: "Every inspection at a glance — compliant, non-compliant and incomplete, updating in real time." },
  { key: "ai", nav: 1, icon: Sparkles, title: "AI Photo Assessment",
    caption: "AI reviews evidence photos, scores confidence and drafts the finding — the inspector decides." },
  { key: "statutory", nav: 2, icon: ScrollText, title: "Statutory Deadline Tracker",
    caption: "Notices, directions and re-inspections tracked with automatic countdowns and reminders." },
  { key: "pdf", nav: 3, icon: FileText, title: "Professional PDF Reports",
    caption: "Branded certificates and non-compliance notices generated in seconds, ready to send." },
];

const NAV_ITEMS = [
  { label: "Dashboard", icon: LayoutDashboard },
  { label: "AI Assist", icon: Sparkles },
  { label: "Statutory", icon: ScrollText },
  { label: "Reports", icon: FileText },
  { label: "Team", icon: ClipboardList },
];

const ease = [0.22, 1, 0.36, 1];
const sceneMotion = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration: 0.5, ease },
};

/* ----------------------------------------------------------- scene: dashboard */
const stat = (label, value, tone, pct) => ({ label, value, tone, pct });
const STATS = [
  stat("Total", "248", "#93a4c8", 100),
  stat("Compliant", "191", "#34d399", 77),
  stat("Non-compliant", "42", "#f87171", 17),
  stat("Incomplete", "15", "#fbbf24", 6),
];
const DASH_ROWS = [
  ["12 Example St, Penrith NSW", "compliant"],
  ["8 Harbour Rd, Manly NSW", "non"],
  ["44 Ridge Ave, Katoomba NSW", "incomplete"],
];
const SceneDashboard = () => (
  <motion.div key="dashboard" {...sceneMotion} className="p-4 sm:p-5 space-y-3">
    <div className="grid grid-cols-4 gap-2 sm:gap-3">
      {STATS.map((s, i) => (
        <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + i * 0.08, ease }}
          className="rounded-lg border border-white/10 p-2.5 sm:p-3" style={{ background: ROW }}>
          <div className="text-xl sm:text-2xl font-bold" style={{ color: s.tone }}>{s.value}</div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 font-semibold truncate">{s.label}</div>
          <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: s.tone }}
              initial={{ width: 0 }} animate={{ width: `${s.pct}%` }} transition={{ delay: 0.25 + i * 0.08, duration: 0.7, ease }}/>
          </div>
        </motion.div>
      ))}
    </div>
    <div className="space-y-2">
      {DASH_ROWS.map(([addr, st], i) => (
        <motion.div key={addr} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 + i * 0.1, ease }}
          className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2.5" style={{ background: ROW }}>
          {st === "compliant" ? <CircleCheck size={15} className="text-emerald-400 shrink-0"/>
            : st === "non" ? <CircleX size={15} className="text-red-400 shrink-0"/>
            : <Timer size={15} className="text-amber-400 shrink-0"/>}
          <span className="text-[11px] sm:text-xs text-slate-300 flex-1 truncate">{addr}</span>
          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${
            st === "compliant" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
              : st === "non" ? "bg-red-500/15 text-red-300 border-red-400/30"
              : "bg-amber-500/15 text-amber-300 border-amber-400/30"}`}>
            {st === "non" ? "Non-compliant" : st === "incomplete" ? "Incomplete" : "Compliant"}
          </span>
        </motion.div>
      ))}
    </div>
  </motion.div>
);

/* ----------------------------------------------------------- scene: AI */
const SceneAI = () => (
  <motion.div key="ai" {...sceneMotion} className="p-4 sm:p-5">
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-4">
        <div className="relative rounded-lg border border-white/10 overflow-hidden aspect-[3/4]"
          style={{ background: "linear-gradient(135deg,#1E1B4B,#13182A)" }}>
          <div className="absolute inset-0 flex items-center justify-center"><Camera size={22} className="text-white/20"/></div>
          <motion.div className="absolute left-0 right-0 h-6" style={{ background: `linear-gradient(180deg, transparent, ${MINT}55, transparent)` }}
            initial={{ top: "-10%" }} animate={{ top: ["-10%", "100%"] }}
            transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity, repeatDelay: 0.4 }}/>
          <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/40 text-[8px] text-slate-300">Gate — photo 1</div>
        </div>
      </div>
      <div className="col-span-8 space-y-2.5">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, ease }}
          className="flex items-center gap-2 rounded-lg border px-3 py-2.5"
          style={{ background: "rgba(248,113,113,0.10)", borderColor: "rgba(248,113,113,0.3)" }}>
          <CircleX size={15} className="text-red-400"/>
          <span className="text-[11px] sm:text-xs font-bold text-red-300">Non-compliant detected</span>
          <span className="ml-auto text-[9px] font-semibold text-slate-400">confidence</span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: MINT, color: NAVY }}>92%</span>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="rounded-lg border border-white/10 p-3" style={{ background: ROW }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={11} style={{ color: MINT }}/>
            <span className="text-[9px] uppercase tracking-wide font-bold text-slate-400">AI-drafted finding</span>
          </div>
          {["Gate does not self-close from all latch positions.", "Non-compliant with AS 1926.1 — Clause 2.4.",
            "Recommended: adjust or replace the self-closing hinge."].map((t, i) => (
            <motion.p key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.25, ease }} className="text-[10px] sm:text-[11px] text-slate-300 leading-relaxed mb-1">{t}</motion.p>
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-md text-[10px] font-bold" style={{ background: MINT, color: NAVY }}>Accept finding</span>
          <span className="px-3 py-1.5 rounded-md text-[10px] font-semibold border border-white/15 text-slate-300">Edit</span>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

/* ----------------------------------------------------------- scene: statutory */
const STAT_ROWS = [
  { t: "Notice of Intention — 8 Harbour Rd", days: 6, tone: "#f87171", tag: "NOI" },
  { t: "Direction to comply — 8 Harbour Rd", days: 22, tone: "#fbbf24", tag: "Direction" },
  { t: "Re-inspection due — 12 Example St", days: 34, tone: "#34d399", tag: "Re-inspect" },
];
const SceneStatutory = () => (
  <motion.div key="statutory" {...sceneMotion} className="p-4 sm:p-5 space-y-2.5">
    <div className="flex items-center gap-2 mb-1">
      <span className="px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wide flex items-center gap-1"
        style={{ background: "rgba(251,146,60,0.15)", color: "#fdba74" }}><Bell size={10}/>3 active clocks</span>
    </div>
    {STAT_ROWS.map((r, i) => (
      <motion.div key={r.t} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.15 + i * 0.12, ease }}
        className="rounded-lg border border-white/10 px-3 py-2.5" style={{ background: ROW }}>
        <div className="flex items-center gap-2.5">
          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border border-white/15 text-slate-300">{r.tag}</span>
          <span className="text-[11px] sm:text-xs text-slate-300 flex-1 truncate">{r.t}</span>
          <div className="text-right">
            <div className="text-sm font-bold leading-none" style={{ color: r.tone }}>{r.days}<span className="text-[9px] ml-0.5">d</span></div>
            <div className="text-[8px] text-slate-500 uppercase tracking-wide">remaining</div>
          </div>
        </div>
        <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{ background: r.tone }}
            initial={{ width: 0 }} animate={{ width: `${Math.max(12, 100 - r.days * 2)}%` }}
            transition={{ delay: 0.3 + i * 0.12, duration: 0.8, ease }}/>
        </div>
      </motion.div>
    ))}
  </motion.div>
);

/* ----------------------------------------------------------- scene: pdf */
const ScenePDF = () => (
  <motion.div key="pdf" {...sceneMotion} className="p-4 sm:p-5">
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-5 space-y-2">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2.5" style={{ background: ROW }}>
          <FileText size={14} style={{ color: MINT }}/>
          <span className="text-[10px] sm:text-[11px] text-slate-300 font-semibold">Generating report…</span>
        </motion.div>
        <div className="rounded-lg border border-white/10 p-3 space-y-2" style={{ background: ROW }}>
          {["Cover & branding", "Findings & photos", "Australian Standard refs", "Re-inspection clauses"].map((t, i) => (
            <motion.div key={t} initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 + i * 0.35, ease }}
              className="flex items-center gap-2">
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 + i * 0.35, ease }}>
                <CircleCheck size={13} className="text-emerald-400"/></motion.span>
              <span className="text-[10px] sm:text-[11px] text-slate-300">{t}</span>
            </motion.div>
          ))}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9, ease }}
            className="flex items-center gap-2 pt-1">
            <span className="px-3 py-1.5 rounded-md text-[10px] font-bold inline-flex items-center gap-1.5" style={{ background: MINT, color: NAVY }}>
              <Download size={11}/>Download PDF</span>
            <span className="text-[9px] text-slate-500">4 pages · 1.2 MB</span>
          </motion.div>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 18, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ delay: 0.35, duration: 0.6, ease }}
        className="col-span-7 rounded-lg overflow-hidden shadow-2xl border border-white/10" style={{ background: "#ffffff" }}>
        <div className="h-6 flex items-center px-3" style={{ background: NAVY }}>
          <div className="w-6 h-2 rounded-sm" style={{ background: MINT }}/>
          <span className="ml-auto text-[7px] font-bold tracking-wide text-white/70">COMPLIANCE HUB</span>
        </div>
        <div className="p-3 space-y-1.5" style={{ background: "#ffffff" }}>
          <div className="text-[9px] font-bold" style={{ color: NAVY }}>NOTICE OF NON-COMPLIANCE</div>
          <div className="h-1 w-10 rounded" style={{ background: MINT }}/>
          {[90, 70, 82, 60].map((w, i) => (
            <motion.div key={i} initial={{ width: 0 }} animate={{ width: `${w}%` }} transition={{ delay: 0.6 + i * 0.12, ease }}
              className="h-1 rounded" style={{ background: "#cbd5e1" }}/>
          ))}
          <div className="grid grid-cols-3 gap-1 pt-1">
            {[0, 1, 2].map((i) => (
              <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 + i * 0.15 }}
                className="aspect-square rounded flex items-center justify-center" style={{ background: "#eef2f7", border: "1px solid #e2e8f0" }}>
                <Camera size={10} style={{ color: "#94a3b8" }}/></motion.div>
            ))}
          </div>
          {[75, 55].map((w, i) => (
            <motion.div key={i} initial={{ width: 0 }} animate={{ width: `${w}%` }} transition={{ delay: 1.4 + i * 0.12, ease }}
              className="h-1 rounded" style={{ background: "#cbd5e1" }}/>
          ))}
        </div>
      </motion.div>
    </div>
  </motion.div>
);

const SCENE_COMPONENTS = { dashboard: SceneDashboard, ai: SceneAI, statutory: SceneStatutory, pdf: ScenePDF };

/* ----------------------------------------------------------- main */
export default function HeroDemo({ variant = "captioned", forceScene = null, interval = 3800 }) {
  const [idx, setIdx] = useState(forceScene ?? 0);

  useEffect(() => {
    if (forceScene !== null) { setIdx(forceScene); return; }
    const id = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), interval);
    return () => clearInterval(id);
  }, [forceScene, interval]);

  const scene = SCENES[idx];
  const SceneBody = SCENE_COMPONENTS[scene.key];
  const captioned = variant === "captioned";

  return (
    <div className="relative mx-auto max-w-4xl" aria-hidden="true">
      <div className="absolute -inset-8 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(0,242,195,0.18), transparent)" }}/>
      <div className="relative rounded-xl border border-white/10 overflow-hidden shadow-2xl" style={{ background: PANEL }}>
        {/* browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10" style={{ background: DARK }}>
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/70"/><span className="w-2.5 h-2.5 rounded-full bg-amber-400/70"/><span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70"/>
          <span className="ml-3 text-[11px] text-slate-500 font-medium">compliancehubau.com</span>
        </div>
        <div className="grid grid-cols-12 min-h-[300px] sm:min-h-[340px]">
          {/* sidebar */}
          <div className="hidden sm:flex flex-col col-span-3 border-r border-white/10 p-3 gap-1.5" style={{ background: DARK }}>
            {NAV_ITEMS.map((n, i) => {
              const active = i === scene.nav;
              return (
                <div key={n.label} className="flex items-center gap-2 px-2.5 py-2 rounded text-[11px] font-medium transition-colors"
                  style={active ? { background: MINT, color: NAVY } : { color: "#7c89a8" }}>
                  <n.icon size={13} strokeWidth={2.4}/>{n.label}
                </div>
              );
            })}
          </div>
          {/* content */}
          <div className="col-span-12 sm:col-span-9 relative">
            {captioned && (
              <div className="flex items-center gap-2 px-4 sm:px-5 pt-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold"
                  style={{ background: "rgba(0,242,195,0.12)", color: MINT, border: "1px solid rgba(0,242,195,0.25)" }}>
                  <scene.icon size={11}/>{scene.title}
                </span>
              </div>
            )}
            <AnimatePresence mode="wait"><SceneBody/></AnimatePresence>
            {captioned && (
              <AnimatePresence mode="wait">
                <motion.p key={scene.key + "-cap"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="px-4 sm:px-5 pb-4 text-[11px] sm:text-xs text-slate-400 leading-relaxed">{scene.caption}</motion.p>
              </AnimatePresence>
            )}
          </div>
        </div>
        {/* progress dots */}
        <div className="flex items-center justify-center gap-2 py-2.5 border-t border-white/10" style={{ background: DARK }}>
          {SCENES.map((s, i) => (
            <div key={s.key} className="h-1 rounded-full transition-all duration-500"
              style={{ width: i === idx ? 22 : 6, background: i === idx ? MINT : "rgba(255,255,255,0.18)" }}/>
          ))}
        </div>
      </div>
    </div>
  );
}
